import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { Lock, Play, FileText, Loader2 } from "lucide-react";

export default function Library() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get(`/content?category=${active}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, [active]);

  const open = (it) => {
    if (it.locked) { navigate("/membership"); return; }
    navigate(`/library/${it.id}`);
  };

  return (
    <AppLayout>
      <div className="relative overflow-hidden rounded-[2rem] border border-taupe animate-fade-up">
        <img src="/library.webp" alt="" className="absolute inset-0 w-full h-full object-cover object-right" />
        <div className="relative p-8 sm:p-12" style={{ background: "linear-gradient(90deg, #FBF1F4 0%, rgba(250,246,243,0.92) 45%, rgba(250,246,243,0.2) 100%)" }}>
          <span className="text-xs font-medium uppercase tracking-[0.2em] text-[#9B7E8D]">Premium library</span>
          <h1 className="mt-2 font-serif text-5xl tracking-tight text-wine">Learn &amp; grow</h1>
          <p className="mt-3 text-base text-[#6E5862] max-w-md">Trusted, non-clinical education to help you understand your body and care for yourself.</p>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-8 flex gap-2.5 overflow-x-auto pb-2 -mx-1 px-1">
        <button
          data-testid="filter-all"
          onClick={() => setActive("all")}
          className={`whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-medium transition-all ${active === "all" ? "bg-wine text-white" : "bg-white border border-taupe text-[#6E5862] hover:bg-cream"}`}
        >All</button>
        {categories.map((c) => (
          <button
            key={c.slug}
            data-testid={`filter-${c.slug}`}
            onClick={() => setActive(c.slug)}
            className={`whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-medium transition-all ${active === c.slug ? "bg-wine text-white" : "bg-white border border-taupe text-[#6E5862] hover:bg-cream"}`}
          >{c.name}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-wine" strokeWidth={1.5} /></div>
      ) : (
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((it) => (
            <button
              key={it.id}
              data-testid={`content-card-${it.id}`}
              onClick={() => open(it)}
              className="group text-left relative overflow-hidden rounded-3xl bg-white border border-taupe transition-all hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(107,37,84,0.1)]"
            >
              <div className="relative h-44 overflow-hidden">
                <img src={it.image_url} alt={it.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <span className="absolute top-3 left-3 inline-flex items-center rounded-full bg-[#F7E9EF] px-3 py-1 text-xs font-medium text-wine capitalize">{it.category === "glp1" ? "GLP-1" : it.category}</span>
                <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-white/90 px-2.5 py-1 text-xs font-medium text-[#6E5862]">
                  {it.type === "video" ? <Play className="h-3 w-3" /> : <FileText className="h-3 w-3" />}{it.type}
                </span>
                {it.locked && (
                  <div className="absolute inset-0 bg-[#3A1230]/40 flex items-center justify-center backdrop-blur-[2px]">
                    <span className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-wine"><Lock className="h-4 w-4" /> Members only</span>
                  </div>
                )}
              </div>
              <div className="p-6">
                <h3 className="font-serif text-2xl text-[#3A1230] leading-snug">{it.title}</h3>
                <p className="mt-2 text-sm text-[#9B7E8D] line-clamp-2">{it.excerpt}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </AppLayout>
  );
}
